import * as Dialog from "@radix-ui/react-dialog";
import { Send } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Button, cx, Input } from "@/components/ui";
import { api, ApiError, type ProductPatch } from "@/lib/api";

import { t, msg } from "@/lib/i18n";
const EMPTY = { sku: "", name: "", gtin: "", l: "", w: "", h: "", kg: "", origin: "", hs: "", customs: "", fragile: false, bestBefore: false, batch: false };

/** Zahl aus deutscher oder englischer Schreibweise; leer → undefined */
const num = (v: string) => {
  const n = Number(v.replace(",", ".").trim());
  return v.trim() && Number.isFinite(n) && n > 0 ? n : undefined;
};

function Field({ label, hint, className, children }: { label: string; hint?: string; className?: string; children: ReactNode }) {
  return (
    <label className={cx("block min-w-0", className)}>
      <span className="text-[12px] font-medium text-fg-muted">{t(label)}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="text-[11.5px] text-fg-subtle">{t(hint)}</span>}
    </label>
  );
}

/**
 * Artikel manuell anlegen – z. B. Beilagen, Flyer, Verpackungsmaterial oder Artikel ohne Shop.
 * Gespeicherte Felder werden gegen Überschreiben durch spätere Shop-Importe gesperrt.
 */
export function CreateProductDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void }) {
  const { tenantId } = useTenant();
  const [f, setF] = useState(EMPTY);
  const [transfer, setTransfer] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof EMPTY>(k: K, v: (typeof EMPTY)[K]) => setF((x) => ({ ...x, [k]: v }));

  const errors = {
    sku: !f.sku.trim() ? "SKU fehlt" : undefined,
    name: !f.name.trim() ? "Bezeichnung fehlt" : undefined,
    origin: f.origin.trim() && !/^[A-Za-z]{2}$/.test(f.origin.trim()) ? "2-stelliger Ländercode, z. B. DE" : undefined,
  };
  const dimsPartial = [f.l, f.w, f.h].some((x) => x.trim()) && ![f.l, f.w, f.h].every((x) => num(x));

  const submit = async () => {
    setTried(true);
    if (errors.sku || errors.name || errors.origin) return;
    const patch: ProductPatch = {
      ...(f.gtin.trim() ? { gtin: f.gtin.trim() } : {}),
      ...(num(f.l) ? { lengthCm: num(f.l)! } : {}),
      ...(num(f.w) ? { widthCm: num(f.w)! } : {}),
      ...(num(f.h) ? { heightCm: num(f.h)! } : {}),
      ...(num(f.kg) ? { grossWeightKg: num(f.kg)! } : {}),
      ...(f.origin.trim() ? { countryOfOrigin: f.origin.trim().toUpperCase() } : {}),
      ...(f.hs.trim() ? { hsCode: f.hs.trim() } : {}),
      ...(num(f.customs) ? { customsValueMinor: Math.round(num(f.customs)! * 100) } : {}),
      ...(f.fragile ? { fragile: true } : {}),
      ...(f.bestBefore ? { bestBeforeManaged: true } : {}),
      ...(f.batch ? { batchManaged: true } : {}),
    };
    setBusy(true);
    try {
      const r = await api.products.create(tenantId, { sku: f.sku.trim(), name: f.name.trim(), transfer, ...patch });
      if (r.transferQueued) toast.success(`${f.sku.trim()} angelegt und wird ans Lager übertragen`);
      else if (transfer && r.validation?.syncBlocked) toast.warning(`${f.sku.trim()} angelegt – Übertragung erst nach Ergänzung der Stammdaten`, { description: msg("Die fehlenden Felder sind im Artikel markiert.") });
      else toast.success(`${f.sku.trim()} angelegt`);
      setF(EMPTY); setTried(false);
      onOpenChange(false);
      onCreated(r.id);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 409 ? `SKU ${f.sku.trim()} gibt es bereits` : "Artikel konnte nicht angelegt werden");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (k: "fragile" | "bestBefore" | "batch", label: string) => (
    <button type="button" onClick={() => set(k, !f[k])} aria-pressed={f[k]} className={cx("h-8 px-3 rounded-lg border text-[13px]", f[k] ? "border-ink bg-surface-2 font-medium" : "border-border text-fg-muted hover:bg-surface-2")}>
      {t(label)}
    </button>
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(560px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-2xl border border-border bg-surface p-5 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[16px] font-semibold">{t("Artikel anlegen")}</Dialog.Title>
          <Dialog.Description className="text-fg-muted mt-1 text-[13px]">{t("Für Artikel ohne Shop – etwa Beilagen, Flyer oder Verpackungsmaterial. Maße und Gewicht braucht das Lager für die Versandart-Wahl.")}</Dialog.Description>
          <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="mt-4 grid grid-cols-2 gap-3">
            <Field label={t("SKU *")} hint={tried ? errors.sku : undefined}>
              <Input autoFocus value={f.sku} onChange={(e) => set("sku", e.target.value)} placeholder={t("FLYER-HERBST")} className="font-mono" aria-invalid={tried && !!errors.sku} />
            </Field>
            <Field label={t("EAN / GTIN")}>
              <Input value={f.gtin} onChange={(e) => set("gtin", e.target.value)} inputMode="numeric" className="font-mono" />
            </Field>
            <Field label={t("Bezeichnung *")} hint={tried ? errors.name : undefined} className="col-span-2">
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={t("Flyer Herbstaktion A6")} aria-invalid={tried && !!errors.name} />
            </Field>
            <Field label={t("Maße L × B × H")} hint={dimsPartial ? t("Bitte alle drei Kanten angeben") : undefined} className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-1">
                {(["l", "w", "h"] as const).map((k, i) => (
                  <span key={k} className="contents">
                    {i > 0 && <span className="text-fg-subtle">×</span>}
                    <Input value={f[k]} onChange={(e) => set(k, e.target.value)} inputMode="decimal" className="tabular-nums" aria-label={["Länge", "Breite", "Höhe"][i]} />
                  </span>
                ))}
                <span className="text-[12px] text-fg-muted">{t("cm")}</span>
              </div>
            </Field>
            <Field label={t("Gewicht (brutto)")} className="col-span-2 sm:col-span-1">
              <Input value={f.kg} onChange={(e) => set("kg", e.target.value)} inputMode="decimal" suffix={t("kg")} className="tabular-nums" />
            </Field>
            <Field label={t("Ursprungsland")} hint={tried ? errors.origin : undefined}>
              <Input value={f.origin} onChange={(e) => set("origin", e.target.value)} placeholder={t("DE")} maxLength={2} className="font-mono uppercase" />
            </Field>
            <Field label={t("Zolltarifnummer (HS)")}>
              <Input value={f.hs} onChange={(e) => set("hs", e.target.value)} className="font-mono" />
            </Field>
            <Field label={t("Zollwert je Stück")}>
              <Input value={f.customs} onChange={(e) => set("customs", e.target.value)} inputMode="decimal" suffix="€" className="tabular-nums" />
            </Field>
            <div className="col-span-2 flex flex-wrap gap-1.5">
              {toggle("fragile", "Zerbrechlich")}
              {toggle("bestBefore", "MHD-pflichtig")}
              {toggle("batch", "Chargenpflichtig")}
            </div>
            <label className="col-span-2 flex items-start gap-2 rounded-lg border border-border bg-surface-2/40 p-3 text-[13px] cursor-pointer">
              <input type="checkbox" checked={transfer} onChange={(e) => setTransfer(e.target.checked)} className="mt-0.5 accent-ink" />
              <span>
                <span className="font-medium">{t("Direkt ans Lager übertragen")}</span>
                <span className="block text-[12px] text-fg-muted">{t("Sobald die Stammdaten vollständig sind. Sonst später über „An Lager übertragen“ im Artikel.")}</span>
              </span>
            </label>
            <div className="col-span-2 mt-2 flex justify-end gap-2">
              <Dialog.Close asChild><Button type="button" variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
              <Button type="submit" variant="primary" loading={busy} icon={transfer ? <Send className="size-4" /> : undefined}>{transfer ? t("Anlegen & übertragen") : t("Anlegen")}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
